import { useTranslation } from "react-i18next";
import React from "react";
import { Button } from "@/components/ui/button";
import { ShoppingCart } from "lucide-react";
import { ProductSelection } from "@/types/product";
import { useCartCountLabel } from "@/components/orders/use-cart-count-label";

interface MobileCartBarProps {
  selectedProducts: ProductSelection[];
  /** Shopping step: opens the cart drawer. */
  onViewCart?: () => void;
  /** Shopping step: navigates to checkout. */
  onCheckout?: () => void;
  /** Checkout step: goes back to shopping. */
  onBack?: () => void;
  /** Checkout step: places the order (submits to Hajoca). */
  onPlaceOrder?: () => void;
  /** Checkout step: saves order as draft. */
  onSaveDraft?: () => void;
  isSubmitting?: boolean;
  disabled?: boolean;
}

export const MobileCartBar: React.FC<MobileCartBarProps> = ({
  selectedProducts,
  onViewCart,
  onCheckout,
  onBack,
  onPlaceOrder,
  onSaveDraft,
  isSubmitting = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const itemCount = selectedProducts.length;
  const totalUnits = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
  const countLabel = useCartCountLabel(itemCount, totalUnits);

  // Checkout mode — Back + Save Draft + Place Order
  if (onPlaceOrder) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg px-4 py-3 lg:hidden"
        aria-label={t("components.orders.mobileCartBar.checkoutActions")}
      >
        <div className="flex gap-2">
          {onBack && (
            <Button
              variant="outline"
              onClick={onBack}
              disabled={isSubmitting}
              className="flex-shrink-0"
            >
              {t("components.orders.mobileCartBar.back")}
            </Button>
          )}
          {onSaveDraft && (
            <Button
              variant="outline"
              onClick={onSaveDraft}
              disabled={disabled || isSubmitting}
              className="flex-shrink-0"
            >
              {t("components.orders.mobileCartBar.saveDraft")}
            </Button>
          )}
          <Button
            onClick={onPlaceOrder}
            disabled={disabled || isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? t("components.orders.mobileCartBar.placing") : t("components.orders.mobileCartBar.placeOrder")}
          </Button>
        </div>
      </div>
    );
  }

  // Shopping mode — item count + View Cart + Check Out
  if (itemCount === 0) {
    return null;
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg px-4 py-3 lg:hidden"
      aria-label={t("components.orders.mobileCartBar.cartSummary")}
    >
      <div className="flex gap-3 items-center">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mr-auto">
          <ShoppingCart className="h-4 w-4" aria-hidden="true" />
          <span aria-live="polite">{countLabel}</span>
        </div>
        {onViewCart && (
          <Button variant="outline" onClick={onViewCart}>
            {t("components.orders.mobileCartBar.viewCart")}
          </Button>
        )}
        {onCheckout && (
          <Button onClick={onCheckout} disabled={disabled}>
            {t("components.orders.mobileCartBar.checkOut")}
          </Button>
        )}
      </div>
    </div>
  );
};
